'use client'

import {
  type ChangeEvent,
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Configure } from 'react-instantsearch'
import { InstantSearchNext } from 'react-instantsearch-nextjs'
import { useDebounceCallback } from 'usehooks-ts'

import { useAlgoliaConfigContext } from '../context/algoliaConfigContext'
import { useSearchClient } from '../hooks/useSearchClient'

import { useAlgoliaUserToken } from './AlgoliaUserTokenProvider'

type AlgoliaLandingPageSearchContextValue = {
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  searchInputValue: string
  searchQuery: string
}

const AlgoliaLandingPageSearchContext =
  createContext<AlgoliaLandingPageSearchContextValue | null>(null)

export const useAlgoliaLandingPageSearchContext =
  (): AlgoliaLandingPageSearchContextValue => {
    const context = useContext(AlgoliaLandingPageSearchContext)

    if (context === null) {
      throw new Error(
        'useAlgoliaLandingPageSearchContext must be used within a AlgoliaLandingPageSearchProvider',
      )
    }

    return context
  }

type AlgoliaLandingPageSearchProviderProps = {
  analyticsTags?: string[]
  children: ReactNode
  hitsPerPage?: number
  initialQuery?: string
}

const DEFAULT_NUMBER_OF_RESULTS = 5
const MIN_QUERY_LENGTH = 3
const DEBOUNCE_TIME_MS = 500

const useAlgoliaLandingPageSearchControls = (
  initialQuery = '',
): AlgoliaLandingPageSearchContextValue => {
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [searchInputValueState, setSearchInputValueState] = useState(
    initialQuery.length >= MIN_QUERY_LENGTH ? initialQuery : '',
  )

  const debouncedSetSearchQuery = useDebounceCallback(
    setSearchQuery,
    DEBOUNCE_TIME_MS,
  )

  const setSearchInputValue = useCallback(
    (value: string, { updateSearchQuery }: { updateSearchQuery: boolean }) => {
      setSearchInputValueState(value)

      if (!updateSearchQuery) {
        return
      }

      if (value.length < MIN_QUERY_LENGTH) {
        setSearchQuery('')

        return
      }

      debouncedSetSearchQuery(value)
      if (value.length === MIN_QUERY_LENGTH) {
        debouncedSetSearchQuery.flush()
      }
    },
    [debouncedSetSearchQuery],
  )

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchInputValue(event.target.value, { updateSearchQuery: true })
    },
    [setSearchInputValue],
  )

  return useMemo(
    () => ({
      handleInputChange,
      searchInputValue: searchInputValueState,
      searchQuery,
    }),
    [searchQuery, handleInputChange, searchInputValueState],
  )
}

export const AlgoliaLandingPageSearchProvider: FC<
  AlgoliaLandingPageSearchProviderProps
> = ({
  analyticsTags,
  children,
  hitsPerPage = DEFAULT_NUMBER_OF_RESULTS,
  initialQuery,
}) => {
  const { productsAlgoliaIndex } = useAlgoliaConfigContext()
  const algoliaUserToken = useAlgoliaUserToken()
  const searchClient = useSearchClient()

  const algoliaLandingPageSearchContextValue =
    useAlgoliaLandingPageSearchControls(initialQuery)

  return (
    <AlgoliaLandingPageSearchContext.Provider
      value={algoliaLandingPageSearchContextValue}
    >
      <InstantSearchNext
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        indexName={productsAlgoliaIndex}
        searchClient={searchClient}
      >
        <Configure
          analyticsTags={analyticsTags}
          filters="NOT promotion:bundle"
          hitsPerPage={hitsPerPage}
          query={algoliaLandingPageSearchContextValue.searchQuery}
          userToken={algoliaUserToken}
        />
        {children}
      </InstantSearchNext>
    </AlgoliaLandingPageSearchContext.Provider>
  )
}
